import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { resolveEntityTags } from '@/features/shared/tags';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { formatStatusLabel, getStatusTextColor, getStatusToneTextColor, Text } from '@/ui';
import {
  getSingleSelectOptionLabel,
  getSingleSelectOptionValue,
} from '@/ui/composites/grid/gridColumns';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { RunLinkCell, TableTextCell, TagsInlineCell } from '@/ui/composites/table';
import {
  resolveDateTimeLabel,
  resolveIteratorLabel,
} from '@/ui/composites/table/cellContents/shared';
import { getThresholdChipKey } from '@/ui/composites/table/tableUtils';
import { formatDuration } from '@/ui/formatters/formatDuration';
import type { GridColDef } from '@/ui/primitives';
import type { BuildDto, MetricThreshold } from '@flowqa/shared';
import type { ReactElement } from 'react';

const columnFlex = {
  run: 0.75,
  pipeline: 1,
  status: 0.5,
  source: 1,
  branch: 1,
  commit: 1,
  started: 0.5,
  finished: 0.5,
  duration: 0.5,
  tags: 0.75,
};

type BuildColumnsOptions = { showPipelineColumn?: boolean; durationThreshold: MetricThreshold };
const NUMERIC_TEXT_SX = {};
const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

export const buildBuildsColumns = ({
  showPipelineColumn = true,
  durationThreshold,
}: BuildColumnsOptions): Array<GridColDef<BuildDto>> => {
  const stringOperators = getStringFilterOperators();
  const statusOperators = getSingleSelectFilterOperators();

  const runColumn: GridColDef<BuildDto> = {
    field: 'buildKey',
    headerName: 'Run',
    flex: columnFlex.run,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    valueGetter: (_value, row) => row.buildKey ?? null,
    renderCell: (params): ReactElement => {
      const runNumber = resolveBuildRunNumber(params.row.buildKey);
      const label = runNumber !== null ? resolveIteratorLabel(runNumber) : 'View';
      const isLatest = params.row.isLatest === true;
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      const href =
        pipelineSegment && runNumber !== null
          ? `/pipelines/${pipelineSegment}/runs/${runNumber}`
          : '/pipelines';
      const executionLink = <RunLinkCell label={label} href={href} latest={isLatest} />;
      return executionLink;
    },
  };

  const pipelineColumn: GridColDef<BuildDto> = {
    field: 'pipelineName',
    headerName: 'Pipeline',
    flex: columnFlex.pipeline,
    valueGetter: (_value, row) => row.pipelineName ?? null,
    renderCell: (params): ReactElement => {
      const label = params.row.pipelineName;
      if (!label) {
        return (
          <Text role="body" component="span">
            -
          </Text>
        );
      }
      const pipelineSlug = toPipelineSlug(label);
      const pipelineSegment =
        pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      return (
        <TableTextCell
          primary={{
            label,
            href: pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines',
          }}
        />
      );
    },
  };
  pipelineColumn.filterOperators = stringOperators;

  const statusColumn: GridColDef<BuildDto> = {
    field: 'status',
    headerName: 'Status',
    flex: columnFlex.status,
    align: 'left',
    headerAlign: 'left',
    type: 'singleSelect',
    valueOptions: buildStatusOptions,
    getOptionLabel: getSingleSelectOptionLabel,
    getOptionValue: getSingleSelectOptionValue,
    filterOperators: statusOperators,
    renderCell: (params): ReactElement => {
      const status = params.row.status;
      const label = formatStatusLabel(status);
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {label.length > 0 ? label : '-'}
        </Text>
      );
    },
  };

  const startedColumn: GridColDef<BuildDto> = {
    field: 'startedAt',
    headerName: 'Started',
    flex: columnFlex.started,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.startedAt ?? null,
    renderCell: (params): ReactElement => (
      <TableTextCell
        primary={{ label: resolveDateTimeLabel(params.row.startedAt ?? null), sx: NUMERIC_TEXT_SX }}
      />
    ),
  };

  const finishedColumn: GridColDef<BuildDto> = {
    field: 'finishedAt',
    headerName: 'Finished',
    flex: columnFlex.finished,
    align: 'right',
    headerAlign: 'right',
    valueGetter: (_value, row) => row.finishedAt ?? null,
    renderCell: (params): ReactElement => (
      <TableTextCell
        primary={{
          label: resolveDateTimeLabel(params.row.finishedAt ?? null),
          sx: NUMERIC_TEXT_SX,
        }}
      />
    ),
  };

  const durationColumn: GridColDef<BuildDto> = {
    field: 'duration',
    headerName: 'Duration',
    flex: columnFlex.duration,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const startedAt = params.row.startedAt ? new Date(params.row.startedAt) : null;
      const finishedAt = params.row.finishedAt ? new Date(params.row.finishedAt) : null;
      const durationMs =
        startedAt && finishedAt ? Math.max(finishedAt.getTime() - startedAt.getTime(), 0) : null;
      const hasDuration = durationMs !== null;
      const durationSeconds = hasDuration ? durationMs / 1000 : null;
      const chipKey = hasDuration ? getThresholdChipKey(durationSeconds, durationThreshold) : null;
      const tone = mapChipKeyToStatusTone(chipKey);
      return (
        <Text
          role="body"
          component="span"
          sx={[
            NUMERIC_TEXT_SX,
            {
              color: hasDuration
                ? chipKey === null
                  ? 'text.primary'
                  : (theme) => getStatusToneTextColor(theme, tone)
                : 'textSecondary',
            },
          ]}
        >
          {formatDuration(durationMs) || '-'}
        </Text>
      );
    },
  };

  const tagsColumn: GridColDef<BuildDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => <TagsInlineCell tags={resolveEntityTags(params.row)} />,
  };

  return showPipelineColumn
    ? [
        runColumn,
        pipelineColumn,
        statusColumn,
        startedColumn,
        finishedColumn,
        durationColumn,
        tagsColumn,
      ]
    : [runColumn, statusColumn, startedColumn, finishedColumn, durationColumn, tagsColumn];
};
